import { Card } from '../../../molecules/Card/Card';
import {
  isEmpty,
  type ChartRow,
  type ChartBaseProps,
} from '../../../molecules/ChartFrame/chartProps';
import { legendItems, type ChartSeries } from '../../../molecules/ChartFrame/chartTheme';
import { ChartLegend } from '../../../molecules/ChartLegend/ChartLegend';
import { KPI, type KPIMetric } from '../../../molecules/KPI/KPI';
import { ScatterChart } from '../../../organisms/ScatterChart/ScatterChart';
import { PLOT_HEIGHT, cardState, spanClass, type WidgetBlockProps } from '../shared/cardShell';

export type EngagementSpikesView = {
  /**
   * `Biggest spike`: a topic, not a date — the frame's own edit (`Worst day /
   * 12 Mar` → `Biggest spike / Network`). The date and the engagement figure
   * are the badge; the ratio to the topic's median decides which day is named.
   */
  kpis: KPIMetric[];
  /** Daily engagement, one key per topic in scope (the chart plots `series` only). */
  rows: ChartRow[];
  xKey: string;
  /** The five busiest topics — the only ones drawn, and so the only ones the KPI may name. */
  series: ChartSeries[];
  formatValue: (value: number) => string;
  /** Comparison-period reading per row and series; nothing when comparison is off. */
  formatCompare?: ChartBaseProps['formatCompare'];
};

export type EngagementSpikesCardProps = WidgetBlockProps & {
  view?: EngagementSpikesView;
  /** Opens the conversations behind a day (index into `rows`). */
  onViewDetails?: (index: number) => void;
};

/** Figma `947:19576` — sudden peaks against each topic's own normal level. */
export function EngagementSpikesCard({
  status,
  view,
  span = 12,
  chrome,
  onRetry,
  onViewDetails,
}: EngagementSpikesCardProps) {
  /* No series means no rows in scope — the topics are the ones present. */
  const hasData = view !== undefined && view.series.length > 0 && !isEmpty(view.rows, view.series);
  return (
    <Card
      {...chrome}
      className={spanClass(span)}
      emptyLabel="No conversation in this period"
      {...cardState(status, hasData, onRetry)}
      title="Engagement spikes"
      helpText="Highlights days when a topic receives unusually high engagement compared with its usual level. Each dot represents one topic on one day."
      kpi={view && <KPI metrics={view.kpis} />}
      chart={
        view && (
          <ScatterChart
            data={view.rows}
            xKey={view.xKey}
            onViewDetails={onViewDetails}
            series={view.series}
            height={PLOT_HEIGHT}
            legend="none"
            /*
             * A redundant encoding on purpose: a spike is already higher up the
             * axis, and making it bigger too is what makes it findable before the
             * reader has looked at a number.
             */
            sizeBy="value"
            formatValue={view.formatValue}
            formatCompare={view.formatCompare}
          />
        )
      }
      legend={view && <ChartLegend items={legendItems(view.series)} />}
    />
  );
}
